// LSP hook connection state machine.
//
// Extracted from code-browser.ts so the socket refcount and the
// idle → connecting → connected transitions can be exercised without
// spinning up a LiveView hook or CodeMirror instance.
//
// Two pieces of shared state live here:
//
//   1. A module-scope refcounted Phoenix Socket. One socket is shared across
//      every CodeEditor hook that needs LSP; it is created on first acquire
//      and disconnected on final release.
//
//   2. Per-owner `_lspState` + `_lspGen`. Each owner (a CodeEditor hook, in
//      production) holds a generation counter so that an inflight connect
//      can detect it has been superseded by a later attach/detach and bail
//      without clobbering the owner's current state.
//
// Pure where we can, explicit side effects where we must.
// The socket singleton is a necessary evil — exposed as
// `_resetLspSocketForTesting` so tests start from a clean slate.

import type { Socket } from "phoenix";
import type { LSPClient } from "./lsp-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LspState =
  | { readonly kind: "idle" }
  | { readonly kind: "connecting"; readonly gen: number }
  | {
      readonly kind: "connected";
      readonly gen: number;
      readonly client: LSPClient;
      readonly dispose: () => void;
    };

export interface LspConnectionOwner {
  _lspState: LspState;
  _lspGen: number;
}

export type SocketFactory = () => Socket;

export interface AttachDeps {
  socketFactory: SocketFactory;
  connect: (
    socket: Socket,
    sessionId: string,
    language: string,
  ) => Promise<LSPClient>;
  enableLsp: (client: LSPClient, uri: string) => () => void;
}

export interface AttachParams {
  owner: LspConnectionOwner;
  sessionId: string;
  language: string;
  uri: string;
  deps: AttachDeps;
}

// ---------------------------------------------------------------------------
// Shared socket — refcounted singleton
// ---------------------------------------------------------------------------

let sharedLspSocket: Socket | null = null;
let lspSocketRefs = 0;

export function acquireLspSocket(factory: SocketFactory): Socket {
  if (!sharedLspSocket) {
    sharedLspSocket = factory();
  }
  lspSocketRefs++;
  return sharedLspSocket;
}

export function releaseLspSocket(): void {
  if (lspSocketRefs === 0) return;
  lspSocketRefs--;
  if (lspSocketRefs === 0 && sharedLspSocket) {
    sharedLspSocket.disconnect();
    sharedLspSocket = null;
  }
}

// Exposed for tests only — the singleton has process lifetime in production.
export function _resetLspSocketForTesting(): void {
  sharedLspSocket = null;
  lspSocketRefs = 0;
}

// Exposed for tests only — inspect the current ref count.
export function _lspSocketRefsForTesting(): number {
  return lspSocketRefs;
}

// ---------------------------------------------------------------------------
// Attach / detach
// ---------------------------------------------------------------------------

// Idempotent: first detaches any prior state on the owner, then walks
// idle → connecting → connected. The generation counter fences off async
// continuations from superseded attempts.
export async function attachLspConnection(params: AttachParams): Promise<void> {
  const { owner, sessionId, language, uri, deps } = params;

  // Tear down whatever was previously attached (connected or connecting).
  detachLspConnection(owner);

  const gen = ++owner._lspGen;
  owner._lspState = { kind: "connecting", gen };
  const socket = acquireLspSocket(deps.socketFactory);

  let client: LSPClient;
  try {
    client = await deps.connect(socket, sessionId, language);
  } catch (err) {
    console.warn("LSP connection failed:", err);
    releaseLspSocket();
    if (owner._lspGen === gen) {
      owner._lspState = { kind: "idle" };
    }
    return;
  }

  // If a newer attach/detach fired while we were awaiting, this client is
  // stale. Drop it and release our socket share — the newer attempt owns
  // the owner state now.
  if (owner._lspGen !== gen) {
    client.disconnect();
    releaseLspSocket();
    return;
  }

  const dispose = deps.enableLsp(client, uri);
  owner._lspState = { kind: "connected", gen, client, dispose };
}

export function detachLspConnection(owner: LspConnectionOwner): void {
  const state = owner._lspState;
  // Bump gen so any inflight connect fences itself off.
  owner._lspGen++;
  owner._lspState = { kind: "idle" };
  if (state.kind === "connected") {
    state.dispose();
    state.client.disconnect();
    releaseLspSocket();
  }
  // connecting: the inflight promise owns its socket share and releases it
  // when it observes the gen bump.
  // idle: nothing to clean up.
}
